import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

/**
 * Submit an AI job for processing
 * @param {Object} params - Job parameters
 * @param {string} params.feature_id - Feature ID to process
 * @param {string} params.prompt_id - Prompt ID to use
 * @param {boolean} [params.stream] - Whether to stream the response
 * @param {string} [params.idempotency_key] - Optional idempotency key
 * @returns {Promise<{job_id: string}>} Job ID
 */
export const submitAIJob = async ({ feature_id, prompt_id, stream = false, idempotency_key }) => {
  try {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const functionUrl = `${supabaseUrl}/functions/v1/ai-jobs`;

    // Get the current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('Not authenticated');
    }

    logger.info('Submitting AI job:', { feature_id, prompt_id, stream, functionUrl });
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        feature_id,
        prompt_id,
        stream,
        idempotency_key,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      logger.error('AI job submission failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
        feature_id,
        prompt_id,
        url: functionUrl
      });
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error('Error submitting AI job:', error);
    throw error;
  }
};

/**
 * Get job status by ID (uses status endpoint)
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Job status and details with latest run
 */
export const getAIJobStatus = async (jobId) => {
  try {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const functionUrl = `${supabaseUrl}/functions/v1/ai-jobs-status/${jobId}`;

    // Get the current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(functionUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error('Error fetching AI job status:', error);
    throw error;
  }
};

/**
 * Get job with runs
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Job with associated runs
 */
export const getAIJobWithRuns = async (jobId) => {
  try {
    const { data, error } = await supabase
      .from('ai_jobs')
      .select(`
        id,
        status,
        error_message,
        request_payload,
        created_at,
        started_at,
        completed_at,
        ai_runs (
          id,
          tokens_used,
          model,
          latency_ms,
          status,
          result_text,
          result_json,
          error_message,
          created_at
        )
      `)
      .eq('id', jobId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error fetching AI job with runs:', error);
    throw error;
  }
};

/**
 * Poll job status until completion or timeout
 * @param {string} jobId - Job ID
 * @param {Object} options - Polling options
 * @param {number} [options.interval=2000] - Polling interval in ms
 * @param {number} [options.timeout=300000] - Timeout in ms (default 5 minutes)
 * @param {Function} [options.onUpdate] - Callback called on each status update
 * @returns {Promise<Object>} Final job status
 */
export const pollAIJobStatus = async (jobId, options = {}) => {
  const {
    interval = 2000,
    timeout = 300000, // 5 minutes
    onUpdate,
  } = options;

  const startTime = Date.now();
  const terminalStatuses = ['succeeded', 'failed', 'canceled'];

  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        // Check timeout
        if (Date.now() - startTime > timeout) {
          reject(new Error('Polling timeout exceeded'));
          return;
        }

        const job = await getAIJobStatus(jobId);

        // Call update callback if provided
        if (onUpdate) {
          onUpdate(job);
        }

        // Check if job is in terminal state
        if (terminalStatuses.includes(job.status)) {
          resolve(job);
          return;
        }

        // Continue polling
        setTimeout(poll, interval);
      } catch (error) {
        reject(error);
      }
    };

    // Start polling
    poll();
  });
};

/**
 * Cancel an AI job
 * @param {string} jobId - Job ID
 * @returns {Promise<void>}
 */
export const cancelAIJob = async (jobId) => {
  try {
    const { error } = await supabase
      .from('ai_jobs')
      .update({ status: 'canceled' })
      .eq('id', jobId);

    if (error) throw error;
  } catch (error) {
    logger.error('Error canceling AI job:', error);
    throw error;
  }
};

/**
 * Create an AI job (wrapper for submitAIJob with better naming)
 */
export const createAIJob = async (featureId, promptId, stream = false) => {
  return submitAIJob({
    feature_id: featureId,
    prompt_id: promptId,
    stream: stream,
  });
};

/**
 * Stream AI job events via SSE with fallback to polling
 * @param {string} jobId - Job ID
 * @param {Object} callbacks - Event callbacks
 * @param {Function} callbacks.onToken - Called when token event received
 * @param {Function} callbacks.onPrompt - Called when prompt event received
 * @param {Function} callbacks.onStatus - Called when status event received
 * @param {Function} callbacks.onFinal - Called when final event received
 * @param {Function} callbacks.onError - Called when error occurs
 * @returns {Promise<void>}
 */
export const streamAIJob = async (jobId, callbacks = {}) => {
  const { onToken, onPrompt, onStatus, onFinal, onError } = callbacks;

  try {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const functionUrl = `${supabaseUrl}/functions/v1/ai-stream/${jobId}`;

    // Get the current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('Not authenticated');
    }

    // Use fetch with ReadableStream for SSE (supports auth headers)
    try {
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          if (line.startsWith('event: ')) {
            currentEventType = line.substring(7).trim();
            continue;
          }
          
          if (line.startsWith('data: ')) {
            const dataStr = line.substring(6).trim();
            if (!dataStr) continue;
            
            try {
              const data = JSON.parse(dataStr);
              
              // Handle based on event type
              switch (currentEventType) {
                case 'token':
                  if (onToken && data.text) {
                    onToken(data.text);
                  }
                  break;
                case 'prompt':
                  if (onPrompt) {
                    onPrompt(data);
                  }
                  break;
                case 'status':
                  if (onStatus) {
                    onStatus(data);
                  }
                  break;
                case 'final':
                  if (onFinal) {
                    onFinal(data);
                  }
                  return; // Job complete
                case 'error':
                  if (onError) {
                    onError(new Error(data.message || 'Job error'));
                  }
                  return;
                case 'complete':
                  // Stream complete, get final result via polling if needed
                  return;
                default:
                  // Try to infer from data structure
                  if (data.text && onToken) {
                    onToken(data.text);
                  } else if (data.prompt_text && onPrompt) {
                    onPrompt(data);
                  } else if (data.status && onStatus) {
                    onStatus(data);
                  } else if (data.result_text !== undefined && onFinal) {
                    onFinal(data);
                    return;
                  }
              }
            } catch (e) {
              // Ignore parse errors for non-JSON data
            }
            
            currentEventType = null; // Reset after processing
          }
        }
      }

      // If we get here, stream ended without final event
      // Fall back to polling to get final result
      await fallbackToPolling(jobId, callbacks);
      return;
    } catch (sseError) {
      logger.warn('SSE stream error, falling back to polling:', sseError);
      // Fall back to polling
      await fallbackToPolling(jobId, callbacks);
    }
  } catch (error) {
    logger.error('Error setting up stream:', error);
    if (onError) {
      onError(error);
    }
    // Fall back to polling
    await fallbackToPolling(jobId, callbacks);
  }
};

/**
 * Fallback polling implementation (also used as primary method due to EventSource auth limitations)
 */
async function fallbackToPolling(jobId, callbacks) {
  const { onToken, onStatus, onFinal, onError } = callbacks;
  let lastEventId = null;

  const pollInterval = setInterval(async () => {
    try {
      // Get new events
      const { data: events, error } = await supabase
        .from('ai_events')
        .select('id, event_type, data, created_at')
        .eq('job_id', jobId)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });

      if (error) throw error;

      if (events) {
        for (const event of events) {
          if (lastEventId && event.id <= lastEventId) continue;

          switch (event.event_type) {
            case 'token':
              if (onToken && event.data?.text) {
                onToken(event.data.text);
              }
              break;
            case 'status':
              if (onStatus) {
                onStatus(event.data);
              }
              break;
            case 'final':
              if (onFinal) {
                onFinal(event.data);
              }
              clearInterval(pollInterval);
              return;
            case 'error':
              if (onError) {
                onError(new Error(event.data?.message || 'Job error'));
              }
              clearInterval(pollInterval);
              return;
            default:
              break;
          }

          lastEventId = event.id;
        }
      }

      // Check if job is complete
      const job = await getAIJobStatus(jobId);
      if (['succeeded', 'failed', 'canceled'].includes(job.status)) {
        clearInterval(pollInterval);
        if (job.status === 'succeeded' && job.latest_run) {
          if (onFinal) {
            onFinal({
              result_text: job.latest_run.result_text,
              result_json: job.latest_run.result_json,
            });
          }
        }
      }
    } catch (error) {
      logger.error('Error polling events:', error);
      if (onError) {
        onError(error);
      }
      clearInterval(pollInterval);
    }
  }, 1000); // Poll every second
}

